import BotMessageEditor from '@common-components/bt-chatbot/bot-components/bot-message-editor';
import type { Asset } from '@common/types/interface';
import { getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { changeNodeDataThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { openToolsDialog, openToolSettingsModal } from '@common/reducers/ToolSettingsModalReducer';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { 
    Stack, 
    Tooltip, 
    Typography, 
    Alert, 
    Button, 
    Menu, 
    MenuItem, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField,
    Checkbox,
    FormControlLabel,
    Divider,
    Box
} from '@mui/material';
import { useWebDispatch } from '@website/store/web-store';
import React, { useMemo, useState, useEffect } from 'react';
import { useReactFlow } from 'reactflow';
import { validateToolSettings } from './tools/settings/tool-settings-validator';
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface Props {
    id: string;
}

type TimeUnit = 'minutes' | 'hours' | 'days';

interface AgentNodeData {
    type: string;
    prompt?: string;
    asset?: any;
    tools?: string[];
    toolSettings?: Record<string, any>;
    followUpEnabled?: boolean;
    followUpValue?: number;
    followUpUnit?: TimeUnit;
    followUpPrompt?: string;
}

// Define the predefined system prompts with placeholders
const PREDEFINED_PROMPTS = [
    {
        id: 'customer-support',
        name: 'Customer Support (General FAQs)',
        prompt: 'You are a helpful customer support assistant for {{company_name}}. Use the knowledge base to answer customer questions clearly and politely. Do not make up answers — if you don\'t know, say you\'ll forward the request to a human.',
        placeholders: ['company_name']
    },
    {
        id: 'product-info',
        name: 'Product or Service Information Assistant',
        prompt: 'You assist customers by providing accurate information about the products or services offered by {{company_name}}. Only use the knowledge base to answer questions. Be friendly, concise, and informative.',
        placeholders: ['company_name']
    },
    {
        id: 'internal-assistant',
        name: 'Internal Team Knowledge Assistant',
        prompt: 'You are an internal assistant for employees of {{company_name}}. Use the knowledge base to answer questions about internal procedures, policies, tools, and workflows. Do not respond if the information is not in the documents.',
        placeholders: ['company_name']
    },
    {
        id: 'documentation',
        name: 'Documentation Navigator',
        prompt: 'You help users navigate the documentation of {{product_or_service_name}}. When a user asks a question, find the most relevant information in the knowledge base and provide it in a summarized, clear way.',
        placeholders: ['product_or_service_name']
    },
    {
        id: 'policy-assistant',
        name: 'Returns, Warranty, and Policy Info Assistant',
        prompt: 'You answer questions related to returns, refunds, warranties, and company policies for {{company_name}}. Only respond based on information found in the knowledge base.',
        placeholders: ['company_name']
    }
];

export default function AgentNodeContainer({ id }: Props) {
    const dispatch = useAppDispatch();
    const data = useAppSelector((state) => getNodeDataByNodeId(state, id)) as AgentNodeData;
    // Get the available tools from Redux store
    const availableTools = useAppSelector((state) => state.ToolsReducer.availableTools);

    const webDispatch = useWebDispatch();

    // State for template selector menu
    const [promptMenuAnchorEl, setPromptMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedPrompt, setSelectedPrompt] = useState<typeof PREDEFINED_PROMPTS[0] | null>(null);
    const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
    const [isPlaceholderDialogOpen, setIsPlaceholderDialogOpen] = useState(false);

    // Calculate validation status for each tool
    const toolValidationStatus = useMemo(() => {
        const validationResults: Record<string, { isValid: boolean; message: string }> = {};

        if (data?.tools && data?.tools?.length > 0) {
            data?.tools.forEach(toolName => {
                const toolSettings = data?.toolSettings?.[toolName] || {};
                console.log(`Validating tool ${toolName} with settings:`, toolSettings);
                validationResults[toolName] = validateToolSettings(toolName, toolSettings);
                console.log(`Validation result for ${toolName}:`, validationResults[toolName]);
            });
        }

        return validationResults;
    }, [data?.tools, data?.toolSettings]);

    // Check if a tool requires configuration
    const toolRequiresConfiguration = (toolId: string) => {
        // Special case for WhatsApp
        if (toolId.toLowerCase() === 'whatsapp') {
            return false;
        }

        // Find the tool in the available tools
        const toolDef = availableTools.find(tool => tool.id === toolId);

        // If tool is found and explicitly doesn't require configuration, return false
        if (toolDef && toolDef.requiresConfiguration === false) {
            return false;
        }

        // Default to requiring configuration
        return true;
    };

    const type = data?.type;

    // Check if system prompt is empty
    const isSystemPromptEmpty = !data?.prompt || data?.prompt?.trim() === '';

    // Function to handle tool selection
    const handleToolsSelected = (tools: string[]) => {
        console.log('Tools selected:', tools);

        // Save to backend via redux
        dispatch(changeNodeDataThunk({
            id,
            dataToPatch: {
                tools,
                toolSettings: data?.toolSettings || {},
                prompt: data?.prompt || ''
            }
        }));
    };

    // Function to handle prompt change
    const handlePromptChange = (value: { message: string; asset?: Asset }) => {
        // Save to backend via redux
        dispatch(changeNodeDataThunk({
            id,
            dataToPatch: {
                ...data, // Keep existing data
                prompt: value.message
            }
        }));
    };

    // Function to handle follow-up prompt change
    const handleFollowUpPromptChange = (value: { message: string; asset?: Asset }) => {
        dispatch(changeNodeDataThunk({
            id,
            dataToPatch: {
                ...data, // Keep existing data
                followUpPrompt: value.message
            }
        }));
    };

    // Function to handle follow-up enabled change
    const handleFollowUpEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeNodeDataThunk({
            id,
            dataToPatch: {
                ...data, // Keep existing data
                followUpEnabled: event.target.checked
            }
        }));
    };

    // Function to handle tool click
    const handleToolClick = (toolName: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        console.log('Tool clicked:', toolName);

        // Register a callback for when settings are saved


        // Dispatch the action to open the modal using the web store dispatch
        webDispatch(openToolSettingsModal({
            toolName,
            nodeId: id,
            initialSettings: data?.toolSettings?.[toolName] || {},
        }));
    };

    // Function to handle opening the tools dialog
    const handleOpenToolsDialog = (e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        console.log('Opening tools dialog with selected tools:', data?.tools);
        dispatch(openToolsDialog({
            nodeId: id,
        }));
    };

    // Function to handle removing a tool
    const handleRemoveTool = (toolName: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        const updatedTools = (data?.tools || []).filter(tool => tool !== toolName);
        // Also drop the removed tool's settings so removal stays authoritative.
        // Otherwise an orphaned `toolSettings[toolName]` (e.g. a configured KB)
        // would let the FE#199 save-time reconciler re-add the tool the user
        // just removed. Keeps the invariant: settings exist iff the tool is
        // selected.
        const { [toolName]: _removed, ...remainingSettings } = data?.toolSettings || {};
        dispatch(changeNodeDataThunk({
            id,
            dataToPatch: {
                tools: updatedTools,
                toolSettings: remainingSettings,
                prompt: data?.prompt || ''
            }
        }));
    };

    // Template selection functions
    const handlePromptMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setPromptMenuAnchorEl(event.currentTarget);
    };

    const handlePromptMenuClose = () => {
        setPromptMenuAnchorEl(null);
    };

    const handlePromptSelect = (prompt: typeof PREDEFINED_PROMPTS[0]) => {
        setSelectedPrompt(prompt);
        handlePromptMenuClose();
        
        // Reset placeholder values when selecting a new prompt
        const initialPlaceholders: Record<string, string> = {};
        prompt.placeholders.forEach(placeholder => {
            initialPlaceholders[placeholder] = '';
        });
        setPlaceholderValues(initialPlaceholders);
        
        // Open the placeholder dialog
        setIsPlaceholderDialogOpen(true);
    };

    const handlePlaceholderChange = (placeholder: string, value: string) => {
        setPlaceholderValues(prev => ({
            ...prev,
            [placeholder]: value
        }));
    };

    const handleApplyTemplate = () => {
        if (!selectedPrompt) return;
        
        // Replace placeholders in the template
        let finalPrompt = selectedPrompt.prompt;
        Object.entries(placeholderValues).forEach(([placeholder, value]) => {
            const replacementValue = value.trim() || `[Your ${placeholder.replace(/_/g, ' ')} here]`;
            finalPrompt = finalPrompt.replace(new RegExp(`{{${placeholder}}}`, 'g'), replacementValue);
        });
        
        // Apply the template
        handlePromptChange({ message: finalPrompt, asset: data?.asset });
        setIsPlaceholderDialogOpen(false);
    };

    const handlePlaceholderDialogClose = () => {
        setIsPlaceholderDialogOpen(false);
    };

    // Handlers to directly patch followUpValue and followUpUnit
    const handleFollowUpValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value;
        const numValue = rawValue === '' ? undefined : parseInt(rawValue, 10);
        const currentUnit = data?.followUpUnit ?? 'minutes'; // Get current unit

        // Only allow positive integers or undefined (for clearing)
        if (numValue !== undefined && (isNaN(numValue) || numValue < 1)) {
           return; // Or provide feedback
        }
        
        // Avoid dispatching if the value hasn't actually changed
        if (numValue !== data?.followUpValue) {
             dispatch(changeNodeDataThunk({
                id,
                dataToPatch: {
                    ...data,
                    followUpValue: numValue,
                    followUpUnit: currentUnit // Also save the current unit
                }
            }));
        }
    };

    const handleFollowUpUnitChange = (newUnit: string) => {
        const unit = newUnit as TimeUnit;
        const currentValue = data?.followUpValue; // Get current value

         // Avoid dispatching if the value hasn't actually changed
         if (unit !== data?.followUpUnit) {
            dispatch(changeNodeDataThunk({
                id,
                dataToPatch: {
                    ...data,
                    followUpUnit: unit,
                    followUpValue: currentValue // Also save the current value
                }
            }));
        }
    };

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <div className="p-4">
                {/* System Prompt */}
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <Typography variant="subtitle1" className="font-medium text-gray-700">
                            System Prompt
                        </Typography>
                        
                        {/* Template Selection Button */}
                        <Button 
                            variant="outlined" 
                            size="small"
                            startIcon={<LightbulbOutlinedIcon fontSize="small" />}
                            endIcon={<ArrowDropDownIcon />}
                            onClick={handlePromptMenuOpen}
                            sx={{ 
                                borderColor: 'var(--bulk-border)', 
                                color: 'var(--bulk-text-secondary)',
                                '&:hover': { 
                                    backgroundColor: '#f5f5f5',
                                    borderColor: '#ccc'
                                }
                            }}
                        >
                            Choose Template
                        </Button>
                        
                        {/* Template Menu */}
                        <Menu
                            anchorEl={promptMenuAnchorEl}
                            open={Boolean(promptMenuAnchorEl)}
                            onClose={handlePromptMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{ 
                                '& .MuiPaper-root': { 
                                    width: '350px',
                                    maxHeight: '400px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            {PREDEFINED_PROMPTS.map((promptTemplate) => (
                                <MenuItem 
                                    key={promptTemplate.id} 
                                    onClick={() => handlePromptSelect(promptTemplate)}
                                    sx={{ 
                                        padding: '10px 16px',
                                        borderBottom: '1px solid #f0f0f0',
                                        '&:hover': { backgroundColor: '#f5faff' }
                                    }}
                                >
                                    <div className="flex flex-col">
                                        <Typography variant="subtitle2" className="font-medium">
                                            {promptTemplate.name}
                                        </Typography>
                                        <Typography variant="caption" className="text-gray-500 mt-1 line-clamp-2">
                                            {promptTemplate.prompt.substring(0, 100)}...
                                        </Typography>
                                    </div>
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>

                    <BotMessageEditor
                        _message={{ message: data?.prompt || '', asset: data?.asset }}
                        onChange={handlePromptChange}
                        type={type}
                    />

                    {isSystemPromptEmpty && (
                        <Alert
                            severity="warning"
                            className="mt-2"
                            icon={<WarningIcon fontSize="small" />}
                            sx={{
                                '& .MuiAlert-message': {
                                    padding: '2px 0',
                                    fontSize: '0.875rem'
                                }
                            }}
                        >
                            It is recommended to provide a system prompt to tell the agent what its job is,
                            <br />
                            otherwise it may behave in an unexpected manner.
                            <br />
                            You can use the template selector to get started.
                        </Alert>
                    )}
                    
                    {/* Placeholder Dialog */}
                    <Dialog 
                        open={isPlaceholderDialogOpen} 
                        onClose={handlePlaceholderDialogClose}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle>
                            <Typography variant="h6" className="font-medium">
                                Customize Template
                            </Typography>
                            <Typography variant="caption" className="text-gray-500 block mt-1">
                                Replace placeholders in the template with your specific information
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            {selectedPrompt && (
                                <div className="space-y-4 mt-2">
                                    {selectedPrompt.placeholders.map((placeholder) => (
                                        <TextField
                                            key={placeholder}
                                            label={placeholder.replace(/_/g, ' ')}
                                            fullWidth
                                            size="small"
                                            value={placeholderValues[placeholder] || ''}
                                            onChange={(e) => handlePlaceholderChange(placeholder, e.target.value)}
                                            variant="outlined"
                                            placeholder={`Enter your ${placeholder.replace(/_/g, ' ')}`}
                                        />
                                    ))}
                                    
                                    <div className="bg-gray-50 p-3 rounded mt-4 border border-gray-200">
                                        <Typography variant="subtitle2" className="text-gray-700 mb-1 font-medium">
                                            Preview:
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-600 whitespace-pre-line">
                                            {selectedPrompt.prompt.replace(/{{([^}]+)}}/g, (match, placeholder) => {
                                                const value = placeholderValues[placeholder];
                                                return value ? value : `[Your ${placeholder.replace(/_/g, ' ')} here]`;
                                            })}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handlePlaceholderDialogClose} color="inherit">
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleApplyTemplate} 
                                variant="contained" 
                                color="primary"
                            >
                                Apply Template
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>

                {/* Tools Section */}
                <div className="mb-4">
                    <Typography variant="subtitle1" className="font-medium text-gray-700 mb-2">
                        Tools
                    </Typography>
                    <div className="flex justify-end mb-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenToolsDialog();
                            }}
                            className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                        >
                            <AddIcon fontSize="small" className="mr-1" />
                            Add Tools
                        </button>
                    </div>

                    {data?.tools && data?.tools?.length > 0 ? (
                        <div className="space-y-2">
                            {data?.tools?.map((tool) => {
                                const validationResult = toolValidationStatus[tool] || { isValid: true, message: '' };
                                const needsConfiguration = toolRequiresConfiguration(tool);

                                return (
                                    <div
                                        key={tool}
                                        className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                                    >
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-gray-700">{tool}</span>
                                            {!validationResult.isValid && (
                                                <Tooltip title={validationResult.message || "This tool requires configuration"}>
                                                    <WarningIcon
                                                        fontSize="small"
                                                        className="ml-2 text-amber-500"
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                        <div className="flex space-x-1">
                                            {/* Only show settings icon if the tool requires configuration */}
                                            {needsConfiguration && (
                                                <Tooltip title="Configure Tool">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToolClick(tool);
                                                        }}
                                                        className={`p-1 rounded-full hover:bg-gray-100 ${!validationResult.isValid ? 'text-amber-500 animate-pulse' : 'text-gray-500 hover:text-blue-600'}`}
                                                    >
                                                        <SettingsIcon fontSize="small" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Remove Tool">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveTool(tool);
                                                    }}
                                                    className="p-1 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-16 text-gray-400 text-xs text-center px-2">
                            No tools selected. Click "Add Tools" to select tools for this agent.
                        </div>
                    )}
                </div>

                {/* Follow-up Section */} 
                <Divider sx={{ my: 3 }} />
                <div className="mb-4">
                    <Typography variant="subtitle1" className="font-medium text-gray-700 mb-2">
                        Follow-up Settings
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={data?.followUpEnabled || false}
                                onChange={handleFollowUpEnabledChange}
                            />
                        }
                        label="Enable automatic follow-up"
                        sx={{ mb: 1 }}
                    />

                    {data?.followUpEnabled && (
                        <Box sx={{ pl: 4, borderLeft: '2px solid var(--bulk-border)', ml: 1.5, mt: 2 }}>
                            <Typography variant="caption" className="text-gray-600 block mb-2">
                                The agent will re-evaluate the conversation after a period of inactivity and may send a follow-up message.
                            </Typography>
                            
                            {/* Shadcn Duration Input */}
                            <Typography variant="subtitle2" className="font-medium text-gray-700 mb-1">
                                Follow-up after
                            </Typography>
                            <div className="flex items-center space-x-2 mb-2">
                                <Input
                                    type="number"
                                    placeholder="e.g., 30"
                                    value={data?.followUpValue ?? ''}
                                    onChange={handleFollowUpValueChange}
                                    min={1}
                                    className="w-24"
                                />
                                <Select 
                                    value={data?.followUpUnit ?? 'minutes'}
                                    onValueChange={handleFollowUpUnitChange}
                                >
                                    <SelectTrigger className="w-[120px]">
                                        <SelectValue placeholder="Select unit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="minutes">Minutes</SelectItem>
                                        <SelectItem value="hours">Hours</SelectItem>
                                        <SelectItem value="days">Days</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Typography variant="caption" className="text-gray-500 block mb-3">
                                Time after the last user message to trigger follow-up evaluation.
                            </Typography>

                            {/* Follow-up Prompt */}
                            <Typography variant="subtitle2" className="font-medium text-gray-700 mb-1">
                                Follow-up System Prompt (Optional)
                            </Typography>
                            <Typography variant="caption" className="text-gray-600 block mb-2">
                                Provide instructions for the agent on how and when to follow up. If left empty, a default behavior will be used.
                            </Typography>
                            <BotMessageEditor
                                _message={{ message: data?.followUpPrompt || '', asset: undefined }}
                                onChange={handleFollowUpPromptChange}
                                type={type}
                            />
                        </Box>
                    )}
                </div>
            </div>
        </Stack>
    );
} 